import React, { useEffect, useState } from "react";

import Paper from "@material-ui/core/Paper";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import TextField from "@material-ui/core/TextField";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Divider from "@material-ui/core/Divider";
import ButtonGroup from "@material-ui/core/ButtonGroup";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";

import {
  format,
  subDays,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  addMonths,
  subMonths,
} from "date-fns";

import { i18n } from "../../translate/i18n";
import Chart from "./Chart";
import api from "../../services/api";
import {
  getDashboardReport,
  getDashboardContactReport,
  listFunnelStageNames,
} from "../../services/dashboard";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  fixedHeightPaper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column",
    height: 280,
  },
  customFixedHeightPaper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "hidden",
    flexDirection: "column",
    height: 120,
    cursor: "pointer",
    transition: "box-shadow 0.15s",
    "&:hover": {
      boxShadow: "0 0 0 2px " + theme.palette.primary.main + " inset",
    },
  },
  filterPaper: {
    padding: theme.spacing(2),
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    alignItems: "center",
  },
  formControl: {
    minWidth: 160,
  },
  dateField: {
    minWidth: 160,
  },
  tableTitle: {
    marginBottom: theme.spacing(1),
  },
  tableContainer: {
    padding: theme.spacing(2),
    overflow: "auto",
    maxHeight: 340,
  },
  rankBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 28,
    height: 28,
    borderRadius: "50%",
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontSize: 13,
    fontWeight: 700,
  },
  statChip: {
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 600,
    background: theme.palette.action.selected,
    color: theme.palette.text.primary,
  },
  statChipClickable: {
    cursor: "pointer",
    "&:hover": {
      background: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
  },
  stageDot: {
    display: "inline-block",
    width: 10,
    height: 10,
    minWidth: 10,
    borderRadius: "50%",
    marginRight: 8,
  },
  quickBtns: {
    marginBottom: 0,
  },
  activePeriod: {
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    "&:hover": {
      background: theme.palette.primary.dark,
    },
  },
}));

const STATUS_LABELS = {
  open: "En servicio",
  pending: "Esperando",
  closed: "Finalizado",
};

// --- Subcomponente: tabla de tickets por agente (recibe los datos ya
// agregados por el backend — services/dashboard.js -> getDashboardReport) ---
const TicketsByUser = ({ rows, loading, classes, onSelectAgent }) => {
  if (loading) return (
    <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
      <CircularProgress size={28} />
    </div>
  );

  if (rows.length === 0) return (
    <Typography variant="body2" color="textSecondary" align="center" style={{ padding: 16 }}>
      Sin datos para el período y filtros seleccionados
    </Typography>
  );

  return (
    <Table size="small" stickyHeader>
      <TableHead>
        <TableRow>
          <TableCell>#</TableCell>
          <TableCell>Agente</TableCell>
          <TableCell align="center">En servicio</TableCell>
          <TableCell align="center">Esperando</TableCell>
          <TableCell align="center">Finalizado</TableCell>
          <TableCell align="center">Total</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, i) => (
          <TableRow key={row.userId || "none"} hover>
            <TableCell>
              <span className={classes.rankBadge}>{i + 1}</span>
            </TableCell>
            <TableCell><strong>{row.name}</strong></TableCell>
            <TableCell align="center">
              <span
                className={`${classes.statChip} ${classes.statChipClickable}`}
                title="Ver qué contactos están en servicio"
                onClick={() => onSelectAgent(row, "open")}
              >
                {row.open}
              </span>
            </TableCell>
            <TableCell align="center">
              <span
                className={`${classes.statChip} ${classes.statChipClickable}`}
                title="Ver qué contactos están esperando"
                onClick={() => onSelectAgent(row, "pending")}
              >
                {row.pending}
              </span>
            </TableCell>
            <TableCell align="center">
              <span
                className={`${classes.statChip} ${classes.statChipClickable}`}
                title="Ver qué contactos finalizaron"
                onClick={() => onSelectAgent(row, "closed")}
              >
                {row.closed}
              </span>
            </TableCell>
            <TableCell align="center">
              <span
                className={`${classes.statChip} ${classes.statChipClickable}`}
                title="Ver qué contactos generaron más chats"
                onClick={() => onSelectAgent(row)}
              >
                {row.total}
              </span>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

// --- Dashboard principal ---
const Dashboard = () => {
  const classes = useStyles();

  // Conexiones
  const [whatsapps, setWhatsapps] = useState([]);
  const [selectedWhatsapp, setSelectedWhatsapp] = useState("");

  // Usuarios
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");

  // Fecha del gráfico (un solo día)
  const todayStr = format(new Date(), "yyyy-MM-dd");
  const [chartDate, setChartDate] = useState(todayStr);

  // Rango para tabla de usuarios
  const [dateFrom, setDateFrom] = useState(todayStr);
  const [dateTo, setDateTo] = useState(todayStr);
  const [activePeriod, setActivePeriod] = useState("today");

  // Embudo/etapa (ej. "Postventa" — existe como etapa en más de un embudo)
  const [funnelStages, setFunnelStages] = useState([]);
  const [selectedFunnelStage, setSelectedFunnelStage] = useState("");

  // Reporte unificado (tarjetas + tabla por agente) — una sola fuente para
  // que todos los números respeten los mismos filtros.
  const [report, setReport] = useState({ totals: { open: 0, pending: 0, closed: 0 }, byAgent: [] });
  const [reportLoading, setReportLoading] = useState(false);

  // Drill-down: contactos que más chats generaron con un agente (solo
  // contactos ya etiquetados — excluye prospectos sin calificar)
  const [contactDrilldown, setContactDrilldown] = useState({
    open: false,
    loading: false,
    agentName: "",
    status: undefined,
    rows: [],
  });

  // Cargar conexiones, usuarios y etapas de embudo
  useEffect(() => {
    api.get("/whatsapp").then(({ data }) => setWhatsapps(data)).catch(() => {});
    api.get("/users").then(({ data }) => setUsers(data.users || [])).catch(() => {});
    listFunnelStageNames().then(setFunnelStages).catch(() => {});
  }, []);

  // Reporte: tarjetas (En servicio / Esperando / Finalizado) + tabla por agente
  useEffect(() => {
    setReportLoading(true);
    const delay = setTimeout(() => {
      getDashboardReport({
        whatsappId: selectedWhatsapp || undefined,
        userId: selectedUser || undefined,
        funnelStageName: selectedFunnelStage || undefined,
        dateFrom,
        dateTo,
      })
        .then(setReport)
        .catch(toastError)
        .finally(() => setReportLoading(false));
    }, 400);
    return () => clearTimeout(delay);
  }, [selectedWhatsapp, selectedUser, selectedFunnelStage, dateFrom, dateTo]);

  // row.userId === undefined => sin filtrar por agente (usa el filtro global
  // "Agente" tal cual esté); row.userId === null => "Sin asignar" puntual
  // (desde la tabla, donde cada fila ya es un agente o grupo específico).
  const openContactDrilldown = (row, status) => {
    setContactDrilldown({
      open: true,
      loading: true,
      agentName: row.name,
      status,
      rows: [],
    });
    getDashboardContactReport({
      whatsappId: selectedWhatsapp || undefined,
      userId:
        row.userId !== undefined
          ? (row.userId ? String(row.userId) : "none")
          : (selectedUser || undefined),
      funnelStageName: selectedFunnelStage || undefined,
      status,
      dateFrom,
      dateTo,
    })
      .then(rows => setContactDrilldown(prev => ({ ...prev, loading: false, rows })))
      .catch(err => {
        toastError(err);
        setContactDrilldown(prev => ({ ...prev, loading: false }));
      });
  };

  const openStatusDrilldown = (status) => {
    const agentName = selectedUser
      ? users.find(u => String(u.id) === selectedUser)?.name || "Agente"
      : "Todos los agentes";
    openContactDrilldown({ name: agentName }, status);
  };

  const closeContactDrilldown = () => {
    setContactDrilldown(prev => ({ ...prev, open: false }));
  };

  const setPeriod = (period) => {
    setActivePeriod(period);
    const today = new Date();
    if (period === "today") {
      const d = format(today, "yyyy-MM-dd");
      setDateFrom(d); setDateTo(d); setChartDate(d);
    } else if (period === "yesterday") {
      const d = format(subDays(today, 1), "yyyy-MM-dd");
      setDateFrom(d); setDateTo(d); setChartDate(d);
    } else if (period === "week") {
      setDateFrom(format(startOfWeek(today, { weekStartsOn: 1 }), "yyyy-MM-dd"));
      setDateTo(format(endOfWeek(today, { weekStartsOn: 1 }), "yyyy-MM-dd"));
      setChartDate(format(today, "yyyy-MM-dd"));
    } else if (period === "month") {
      setDateFrom(format(startOfMonth(today), "yyyy-MM-dd"));
      setDateTo(format(endOfMonth(today), "yyyy-MM-dd"));
      setChartDate(format(today, "yyyy-MM-dd"));
    }
  };

  // Flechas para moverse rápido de mes en mes, a partir del mes del rango
  // actualmente seleccionado (sin importar cómo se llegó a él).
  const shiftMonth = (direction) => {
    const anchor = direction > 0 ? addMonths(new Date(dateFrom), 1) : subMonths(new Date(dateFrom), 1);
    setDateFrom(format(startOfMonth(anchor), "yyyy-MM-dd"));
    setDateTo(format(endOfMonth(anchor), "yyyy-MM-dd"));
    setChartDate(format(anchor, "yyyy-MM-dd"));
    setActivePeriod("month");
  };

  return (
    <div>
      <Container maxWidth="lg" className={classes.container}>
        <Grid container spacing={3}>

          {/* === FILTROS === */}
          <Grid item xs={12}>
            <Paper className={classes.filterPaper}>
              <Typography variant="subtitle2" style={{ marginRight: 8, fontWeight: 700 }}>
                Filtros:
              </Typography>

              {/* Conexión */}
              <FormControl className={classes.formControl} size="small" variant="outlined">
                <InputLabel>Conexión</InputLabel>
                <Select
                  value={selectedWhatsapp}
                  onChange={e => setSelectedWhatsapp(e.target.value)}
                  label="Conexión"
                >
                  <MenuItem value=""><em>Todas</em></MenuItem>
                  {whatsapps.map(w => (
                    <MenuItem key={w.id} value={String(w.id)}>{w.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Usuario */}
              <FormControl className={classes.formControl} size="small" variant="outlined">
                <InputLabel>Agente</InputLabel>
                <Select
                  value={selectedUser}
                  onChange={e => setSelectedUser(e.target.value)}
                  label="Agente"
                >
                  <MenuItem value=""><em>Todos</em></MenuItem>
                  {users.map(u => (
                    <MenuItem key={u.id} value={String(u.id)}>{u.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Embudo / etapa (ej. "Postventa" — existe en más de un embudo).
                  Colores iguales a los del Kanban (vienen de FunnelStage.color). */}
              <FormControl className={classes.formControl} size="small" variant="outlined">
                <InputLabel>Embudo</InputLabel>
                <Select
                  displayEmpty
                  value={selectedFunnelStage}
                  onChange={e => setSelectedFunnelStage(e.target.value)}
                  label="Embudo"
                  renderValue={value => {
                    if (!value) return <em>Todos</em>;
                    const stage = funnelStages.find(s => s.name === value);
                    return (
                      <span style={{ display: "flex", alignItems: "center" }}>
                        <span
                          className={classes.stageDot}
                          style={{ background: stage ? stage.color : "#7C4DFF" }}
                        />
                        {value}
                      </span>
                    );
                  }}
                >
                  <MenuItem value=""><em>Todos</em></MenuItem>
                  {funnelStages.map(stage => (
                    <MenuItem key={stage.name} value={stage.name}>
                      <span className={classes.stageDot} style={{ background: stage.color }} />
                      {stage.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Divider orientation="vertical" flexItem />

              {/* Período rápido */}
              <ButtonGroup size="small" className={classes.quickBtns}>
                {[
                  { key: "today", label: "Hoy" },
                  { key: "yesterday", label: "Ayer" },
                  { key: "week", label: "Semana" },
                  { key: "month", label: "Mes" },
                ].map(p => (
                  <Button
                    key={p.key}
                    onClick={() => setPeriod(p.key)}
                    className={activePeriod === p.key ? classes.activePeriod : ""}
                    variant={activePeriod === p.key ? "contained" : "outlined"}
                    color={activePeriod === p.key ? "primary" : "default"}
                  >
                    {p.label}
                  </Button>
                ))}
              </ButtonGroup>

              {/* Flechas para saltar de mes en mes */}
              <IconButton size="small" onClick={() => shiftMonth(-1)} title="Mes anterior">
                <ChevronLeftIcon />
              </IconButton>
              <IconButton size="small" onClick={() => shiftMonth(1)} title="Mes siguiente">
                <ChevronRightIcon />
              </IconButton>

              {/* Rango personalizado */}
              <TextField
                label="Desde"
                type="date"
                size="small"
                variant="outlined"
                className={classes.dateField}
                value={dateFrom}
                onChange={e => { setDateFrom(e.target.value); setActivePeriod("custom"); }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Hasta"
                type="date"
                size="small"
                variant="outlined"
                className={classes.dateField}
                value={dateTo}
                onChange={e => { setDateTo(e.target.value); setActivePeriod("custom"); }}
                InputLabelProps={{ shrink: true }}
              />
            </Paper>
          </Grid>

          {/* === CONTADORES (clic para ver qué contactos los componen) === */}
          <Grid item xs={4}>
            <Paper
              className={classes.customFixedHeightPaper}
              onClick={() => openStatusDrilldown("open")}
              title="Ver qué contactos están en servicio"
            >
              <Typography component="h3" variant="h6" color="primary" paragraph>
                {i18n.t("dashboard.messages.inAttendance.title")}
              </Typography>
              <Typography component="h1" variant="h4">
                {report.totals.open}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={4}>
            <Paper
              className={classes.customFixedHeightPaper}
              onClick={() => openStatusDrilldown("pending")}
              title="Ver qué contactos están esperando"
            >
              <Typography component="h3" variant="h6" color="primary" paragraph>
                {i18n.t("dashboard.messages.waiting.title")}
              </Typography>
              <Typography component="h1" variant="h4">
                {report.totals.pending}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={4}>
            <Paper
              className={classes.customFixedHeightPaper}
              onClick={() => openStatusDrilldown("closed")}
              title="Ver qué contactos finalizaron"
            >
              <Typography component="h3" variant="h6" color="primary" paragraph>
                {i18n.t("dashboard.messages.closed.title")}
              </Typography>
              <Typography component="h1" variant="h4">
                {report.totals.closed}
              </Typography>
            </Paper>
          </Grid>

          {/* === GRÁFICO (solo muestra un día) === */}
          <Grid item xs={12}>
            <Paper className={classes.fixedHeightPaper}>
              <Chart
                whatsappId={selectedWhatsapp}
                userId={selectedUser}
                selectedDate={chartDate + "T12:00:00.000Z"}
              />
            </Paper>
          </Grid>

          {/* === TABLA TICKETS POR USUARIO === */}
          <Grid item xs={12}>
            <Paper className={classes.tableContainer}>
              <Typography variant="h6" color="primary" className={classes.tableTitle}>
                Tickets por agente
                <Typography variant="caption" color="textSecondary" style={{ marginLeft: 8 }}>
                  {dateFrom === dateTo ? dateFrom : `${dateFrom} → ${dateTo}`}
                </Typography>
              </Typography>
              <TicketsByUser
                rows={report.byAgent}
                loading={reportLoading}
                classes={classes}
                onSelectAgent={openContactDrilldown}
              />
            </Paper>
          </Grid>

        </Grid>
      </Container>

      {/* === DRILL-DOWN: contactos que más chats generaron con un agente === */}
      <Dialog open={contactDrilldown.open} onClose={closeContactDrilldown} maxWidth="sm" fullWidth>
        <DialogTitle>
          {contactDrilldown.agentName} — contactos con más chats
          <Typography variant="caption" color="textSecondary" display="block">
            {dateFrom === dateTo ? dateFrom : `${dateFrom} → ${dateTo}`}
            {contactDrilldown.status && ` · ${STATUS_LABELS[contactDrilldown.status]}`}
            {" · solo contactos etiquetados"}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {contactDrilldown.loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
              <CircularProgress size={28} />
            </div>
          ) : contactDrilldown.rows.length === 0 ? (
            <Typography variant="body2" color="textSecondary" align="center" style={{ padding: 16 }}>
              Sin contactos etiquetados en este período/filtro
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Contacto</TableCell>
                  <TableCell>Número</TableCell>
                  <TableCell align="center">Chats</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contactDrilldown.rows.map((row, i) => (
                  <TableRow key={row.contactId} hover>
                    <TableCell>
                      <span className={classes.rankBadge}>{i + 1}</span>
                    </TableCell>
                    <TableCell><strong>{row.name}</strong></TableCell>
                    <TableCell>{row.number}</TableCell>
                    <TableCell align="center">
                      <span className={classes.statChip}>{row.total}</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;